import { type AbstractUser } from "@/user";
import {
  enumeratedLiterals,
  type EnumeratedLiteralType,
} from "@north-beam/nb-common";

import { type PageId } from "../../constants";

/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type, and the types in this file, will be replaced with the types in the
 * logging/logger/amplitude.ts file.
 */
export type Amplitude_identify<I extends AmplitudeIdentify_Deprecated> = (
  identify: I,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ...args: any[]
) => void;

/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type, and the types in this file, will be replaced with the types in the
 * logging/logger/amplitude.ts file.
 */
export type AmplitudeInit_Deprecated<O> = (
  apiKey: string,
  userId?: string,
  options?: O,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ...args: any[]
) => void;

/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type, and the types in this file, will be replaced with the types in the
 * logging/logger/amplitude.ts file.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type AmplitudeLogEvent_Deprecated = (...args: any[]) => any;

/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type, and the types in this file, will be replaced with the types in the
 * logging/logger/amplitude.ts file.
 */
export type AmplitudeIdentify_Deprecated = {
  set(
    key: string,
    // These types are taken directly from the amplitude package.
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    value: boolean | number | string | any[] | object,
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  ): any;
};

/* Note: This is not the entire type representation of the AmplitudeClient type across the various
   amplitude packages - but it is the minimal representation that is needed for the configuration in
   this module to work.

   We do not want to import the entire type representation of the AmplitudeClient type because it
   would involve installing several different amplitude packages and reconciling the types between
   them, since nb-frontend and nb-client-side use different amplitude plugins. */
/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type, and the types in this file, will be replaced with the types in the
 * logging/logger/amplitude.ts file.
 */
export type AmplitudeClient_Deprecated<O> = {
  logEvent: AmplitudeLogEvent_Deprecated;
  init: AmplitudeInit_Deprecated<O>;
};

/* Note: This is not the entire type representation of the Amplitude package type - but it is the
   minimal representation that is needed for the configuration in this module to work.

   We do not want to import the entire type representation of the Amplitude package type because it
   would involve installing several different amplitude packages and reconciling the types between
   them, since nb-frontend and nb-client-side use different amplitude plugins. */
/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type, and the types in this file, will be replaced with the types in the
 * logging/logger/amplitude.ts file.
 */
export type Amplitude_Deprecated<
  O,
  I extends AmplitudeIdentify_Deprecated,
  IFn extends Amplitude_identify<I>,
> = {
  identify: IFn;
  logEvent: AmplitudeLogEvent_Deprecated;
  Identify: new () => I;
  init: AmplitudeInit_Deprecated<O>;
};

/**
 * @deprecated
 * This will be removed as we migrate Amplitude-related behavior to usage of the {@link Logger}
 * class. This type will be removed.
 */
export type ConfiguredAmplitude<
  U extends AbstractUser,
  A extends Amplitude_Deprecated<O, I, IFn>,
  O,
  I extends AmplitudeIdentify_Deprecated,
  IFn extends Amplitude_identify<I>,
> = {
  identify: (user: U) => void;
  identifyWeb: (user: U) => void;
  initialize: (user?: U) => void;
  logEvent: A["logEvent"];
  prettyLogEvent: PrettyLogEvent<U>;
};

/**
 * @deprecated
 * This value will be replaced with the contents of logging/logger/events.ts.
 */
export const LogEventActions = enumeratedLiterals(
  [
    "onExit",
    "Navigate to",
    "Navigate to page",
    "onDoubleClick",
    "onClick",
    "onOpen",
    "onClose",
    "onChange",
    "onSubmit",
    "onMount",
    "Other",
    "Visited",
    "onSelect",
    "Apply Filters",
    "Clear Filters",
    "Go Back",
    "onNext",
  ] as const,
  {},
);

/**
 * @deprecated
 * This value will be replaced with the contents of logging/logger/events.ts.
 */
export type LogEventAction = EnumeratedLiteralType<typeof LogEventActions>;

/**
 * @deprecated
 * This value will be replaced with the contents of logging/logger/events.ts.
 */
export type LogEventParams<U extends AbstractUser, T = unknown> = {
  page: PageId;
  // Add to this as required
  user: U;
  domain?: string;
  action: LogEventAction;
  additionalActionInfo?: string;
  data?: T;
};

/**
 * @deprecated
 * This value will be replaced with the contents of logging/logger/events.ts.
 */
export const LogMethodNames = {
  [LogEventActions.ONEXIT]: "onExit",
  [LogEventActions.NAVIGATE_TO]: "navigateTo",
  [LogEventActions.NAVIGATE_TO_PAGE]: "navigateToPage",
  [LogEventActions.ONDOUBLECLICK]: "onDoubleClick",
  [LogEventActions.ONCLICK]: "onClick",
  [LogEventActions.ONOPEN]: "onOpen",
  [LogEventActions.ONCHANGE]: "onChange",
  [LogEventActions.ONCLOSE]: "onClose",
  [LogEventActions.ONMOUNT]: "onMount",
  [LogEventActions.VISITED]: "visited",
  [LogEventActions.ONSUBMIT]: "onSubmit",
  [LogEventActions.OTHER]: "other",
  [LogEventActions.ONSELECT]: "onSelect",
  [LogEventActions.APPLY_FILTERS]: "applyFilters",
  [LogEventActions.CLEAR_FILTERS]: "clearFilters",
  [LogEventActions.GO_BACK]: "goBack",
  [LogEventActions.ONNEXT]: "onNext",
} as const;

/**
 * @deprecated
 * This type will be removed as we migrate usage of Amplitude to the centralized Logger.
 */
export type PrettyLogEventAction<U extends AbstractUser, T> = (
  params: Omit<LogEventParams<U, T>, "action">,
) => void;

/**
 * @deprecated
 * This type will be removed as we migrate usage of Amplitude to the centralized Logger.
 */
export type PrettyLogEventActionMethods<U extends AbstractUser> = {
  [key in LogEventAction as key extends keyof typeof LogMethodNames
    ? (typeof LogMethodNames)[key]
    : never]: PrettyLogEventAction<U, unknown>;
};

/**
 * @deprecated
 * This type will be removed as we migrate usage of Amplitude to the centralized Logger.  The
 * {@link Logger.event} method will replace the method that this type represents.
 */
export type PrettyLogEvent<U extends AbstractUser> = {
  <T>(params: LogEventParams<U, T>): void;
} & PrettyLogEventActionMethods<U>;
